import React, { useMemo } from 'react';

import { CategoriesNav } from 'src/components/categories-nav';
import type { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import { RemoteComponentSlot, useRemoteComponent } from 'hooks/use-remote-component';
import type { StoreFrontCategoryTilesProps } from '@dutchiesdk/ecommerce-extensions-sdk';
import { createMapCategoryToRemote } from 'src/theme/provider/commerce-components/bridge/bridge-map-category';

type HomeCategoryTilesProps = {
  categories: DispensaryMenuCategory[];
};

export function HomeCategoryTiles({ categories }: HomeCategoryTilesProps): JSX.Element {
  const { dispensary } = useDispensary();
  const { isDemo, isEmbedded, isKiosk, isMobileEcommApp, isStoreFront } = useUI();
  const categoryTiles = useMemo(() => {
    const mapCategoryToRemote = createMapCategoryToRemote({
      dispensary,
      isDemo,
      isEmbedded,
      isKiosk,
      isMobileEcommApp,
      isStoreFront,
    });

    return categories.map((category) => ({
      // Preserve the raw Home props that the host supplied before ECOMM-13277. They were not
      // part of the SDK contract, but independently deployed themes may still consume them.
      ...category,
      ...mapCategoryToRemote(category),
    }));
  }, [categories, dispensary, isDemo, isEmbedded, isKiosk, isMobileEcommApp, isStoreFront]);
  const NativeCategoryTiles = useMemo(
    () =>
      function NativeHomeCategoryTiles(): JSX.Element {
        return <CategoriesNav categories={categories} />;
      },
    [categories]
  );
  const RemoteCategoryTiles = useRemoteComponent<StoreFrontCategoryTilesProps>(RemoteComponentSlot.CategoryTiles, {
    fallback: NativeCategoryTiles,
  });

  return <RemoteCategoryTiles categories={categoryTiles} surface='home' />;
}
